'use client';

import { useState, useEffect } from 'react';
import Image, { ImageProps } from 'next/image';

interface OptimizedImageProps extends Omit<ImageProps, 'onLoad'> {
  /**
   * Placeholder color while image loads
   */
  placeholderColor?: string;
  
  /**
   * Optional callback when image loads
   */
  onImageLoad?: () => void;
  
  /**
   * Optional performance mark name
   */
  performanceMarkName?: string;
  
  /**
   * Content-visibility CSS property
   */
  contentVisibility?: 'auto' | 'visible' | 'hidden';
  
  /**
   * Mobile-specific sizing behavior
   * - maintain: Keep the same aspect ratio on mobile
   * - adjust: Use a mobile-specific aspect ratio
   * - scale: Scale down proportionally on mobile
   */
  mobileBehavior?: 'maintain' | 'adjust' | 'scale';
  
  /**
   * Mobile-specific aspect ratio (e.g., "1/1" for square on mobile)
   * Only used if mobileBehavior is 'adjust'
   */
  mobileAspectRatio?: string;
}

export default function OptimizedImage({
  src,
  alt,
  width,
  height,
  placeholderColor = '#edf6f5',
  onImageLoad,
  performanceMarkName,
  contentVisibility = 'auto',
  priority = false,
  loading = 'lazy',
  mobileBehavior = 'maintain',
  mobileAspectRatio,
  ...rest
}: OptimizedImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsClient(true);
    
    // Check if we're on a mobile device
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    // Initial check
    checkMobile();
    
    // Listen for resize events
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Calculate aspect ratio for the placeholder
  const getAspectRatio = () => {
    if (mobileBehavior === 'adjust' && mobileAspectRatio && isMobile) {
      return mobileAspectRatio;
    }
    
    return typeof width === 'number' && typeof height === 'number' 
      ? `${width} / ${height}`
      : undefined;
  };

  const aspectRatio = getAspectRatio();

  const handleImageLoad = () => {
    setIsLoaded(true);
    
    // Set performance mark if provided
    if (performanceMarkName && typeof window !== 'undefined' && window.performance) {
      window.performance.mark(performanceMarkName);
    }
    
    // Call optional callback
    if (onImageLoad) {
      onImageLoad();
    }
  };

  // Use proper loading attribute based on priority
  const loadingAttribute = priority ? 'eager' : loading;
  
  // Apply mobile-specific scaling if needed
  const mobileScalingClass = mobileBehavior === 'scale' && isMobile
    ? 'transform scale-90'
    : '';
  
  return (
    <div className={`relative w-full h-full ${mobileScalingClass}`}>
      {/* Image placeholder to prevent layout shift */}
      <div 
        className={`absolute inset-0 z-0 ${isLoaded ? 'opacity-0' : 'opacity-100'}`}
        aria-hidden="true"
        style={{
          backgroundColor: placeholderColor,
          aspectRatio: aspectRatio ? aspectRatio : 'auto',
          transition: 'opacity 0.2s ease-in-out'
        }}
      />
      
      {/* Basic img tag for static exports and SSG */}
      {!isClient && priority && (
        <img 
          src={typeof src === 'string' ? src : ''}
          alt={alt}
          width={typeof width === 'number' ? width : undefined}
          height={typeof height === 'number' ? height : undefined}
          className={`w-full h-auto relative z-10 ${rest.className || ''}`}
          style={{
            objectFit: (rest.style as any)?.objectFit || 'cover',
            contentVisibility,
            aspectRatio: aspectRatio
          }}
        />
      )}
      
      {/* Next.js Image component */}
      {(isClient || !priority) && (
        <Image 
          src={src}
          alt={alt}
          width={width}
          height={height}
          priority={priority}
          loading={loadingAttribute}
          fetchPriority={priority ? 'high' : 'auto'}
          onLoad={handleImageLoad}
          style={{ 
            contentVisibility,
            aspectRatio: aspectRatio || undefined,
            ...(rest.style || {})
          }}
          {...rest}
        />
      )}
    </div>
  );
} 